import { create } from "zustand";
import {
  getConversations,
  getMessages,
  getUnread,
  markRead,
  sendMedia,
  sendText,
} from "@/services/chat";
import { MESSAGE_TYPE, createClientMessageId, lastServerId, mergeMessages } from "@/utils/chat";

const PAGE_SIZE = 40;
const SOUND_KEY = "chat_sound_enabled";

const emptyThread = {
  messages: [],
  conversation: null,
  openSendLogs: [],
  otherLastReadMessageId: 0,
  hasMore: false,
  loading: false,
  loadingOlder: false,
  loaded: false,
  error: "",
};

function readSoundSetting() {
  try {
    return localStorage.getItem(SOUND_KEY) !== "0";
  } catch {
    return true;
  }
}

/** Re-applies read ticks after the counterpart's last-read pointer moves. */
function applyReadTicks(messages, otherLastRead) {
  return messages.map((m) =>
    m.isMine && typeof m.id === "number" ? { ...m, isRead: m.id <= otherLastRead } : m
  );
}

/**
 * «واریز و برداشت» chat state. Everything is REST + polling: the notifier polls the unread
 * counter globally, the page polls the conversation list and the open thread.
 */
const useChatStore = create((set, get) => ({
  unreadTotal: 0,
  latestUnreadId: 0,
  conversations: [],
  conversationsLoaded: false,
  conversationsError: "",
  activeId: null,
  threads: {},
  pageOpen: false,
  soundEnabled: readSoundSetting(),

  setPageOpen: (pageOpen) => set({ pageOpen }),
  setActiveId: (activeId) => set({ activeId }),

  setSoundEnabled: (soundEnabled) => {
    try {
      localStorage.setItem(SOUND_KEY, soundEnabled ? "1" : "0");
    } catch {
      // storage may be unavailable (private mode); the in-memory value still applies
    }
    set({ soundEnabled });
  },

  updateThread: (id, patch) =>
    set((state) => ({
      threads: {
        ...state.threads,
        [id]: { ...emptyThread, ...state.threads[id], ...(typeof patch === "function" ? patch(state.threads[id] ?? emptyThread) : patch) },
      },
    })),

  refreshUnread: async () => {
    const data = await getUnread();
    set({ unreadTotal: data?.total ?? 0, latestUnreadId: data?.latestMessageId ?? 0 });
    return data;
  },

  refreshConversations: async () => {
    try {
      const list = await getConversations();
      set({ conversations: list ?? [], conversationsLoaded: true, conversationsError: "" });
      return list ?? [];
    } catch (error) {
      set({ conversationsLoaded: true, conversationsError: error.message });
      return get().conversations;
    }
  },

  /** First page of a thread (newest messages). */
  loadThread: async (id) => {
    const { updateThread } = get();
    updateThread(id, { loading: true, error: "" });
    try {
      const data = await getMessages({ conversationId: id, take: PAGE_SIZE });
      updateThread(id, (thread) => ({
        messages: applyReadTicks(mergeMessages(thread.messages, data.messages), data.otherLastReadMessageId),
        conversation: data.conversation,
        openSendLogs: data.openSendLogs ?? [],
        otherLastReadMessageId: data.otherLastReadMessageId,
        hasMore: data.hasMore,
        loading: false,
        loaded: true,
      }));
      get().patchConversation(data.conversation);
    } catch (error) {
      updateThread(id, { loading: false, error: error.message });
    }
  },

  /** Polls for messages newer than the last one we have, plus header / read-tick changes. */
  pollThread: async (id) => {
    const thread = get().threads[id];
    if (!thread?.loaded) return get().loadThread(id);
    const afterId = lastServerId(thread.messages);
    try {
      const data = await getMessages({ conversationId: id, afterId: afterId || undefined, take: PAGE_SIZE });
      get().updateThread(id, (current) => ({
        messages: applyReadTicks(mergeMessages(current.messages, data.messages), data.otherLastReadMessageId),
        conversation: data.conversation,
        openSendLogs: data.openSendLogs ?? [],
        otherLastReadMessageId: data.otherLastReadMessageId,
        error: "",
      }));
      get().patchConversation(data.conversation);
      return data.messages?.length ?? 0;
    } catch {
      // transient poll failures are retried on the next tick
      return 0;
    }
  },

  loadOlder: async (id) => {
    const thread = get().threads[id];
    if (!thread?.hasMore || thread.loadingOlder) return;
    const oldest = thread.messages.find((m) => typeof m.id === "number");
    if (!oldest) return;
    get().updateThread(id, { loadingOlder: true });
    try {
      const data = await getMessages({ conversationId: id, beforeId: oldest.id, take: PAGE_SIZE });
      get().updateThread(id, (current) => ({
        messages: applyReadTicks(mergeMessages(current.messages, data.messages), current.otherLastReadMessageId),
        hasMore: data.hasMore,
        loadingOlder: false,
      }));
    } catch {
      get().updateThread(id, { loadingOlder: false });
    }
  },

  patchConversation: (conversation) => {
    if (!conversation) return;
    set((state) => {
      const exists = state.conversations.some((c) => c.id === conversation.id);
      const conversations = exists
        ? state.conversations.map((c) => (c.id === conversation.id ? { ...c, ...conversation } : c))
        : [conversation, ...state.conversations];
      return { conversations };
    });
  },

  /** Marks everything visible in the thread as read, locally first so badges update instantly. */
  markThreadRead: async (id) => {
    const thread = get().threads[id];
    const lastId = thread ? lastServerId(thread.messages) : 0;
    const conversation = get().conversations.find((c) => c.id === id);
    if (!lastId || !conversation?.unreadCount) return;
    set((state) => ({
      unreadTotal: Math.max(0, state.unreadTotal - conversation.unreadCount),
      conversations: state.conversations.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c)),
    }));
    try {
      await markRead({ conversationId: id, lastMessageId: lastId });
    } catch {
      // the next unread poll corrects the badge if this failed
    }
  },

  /** Optimistic send: the bubble shows a clock until the server confirms it. */
  sendMessage: async (id, { text, file, kind, durationSeconds, localUrl, retryOf }) => {
    const clientMessageId = retryOf?.clientMessageId ?? createClientMessageId();
    const type = kind === "voice" ? MESSAGE_TYPE.VOICE : kind === "image" ? MESSAGE_TYPE.IMAGE : MESSAGE_TYPE.TEXT;
    const optimistic = {
      id: retryOf?.id ?? `tmp-${clientMessageId}`,
      clientMessageId,
      type,
      text: kind ? text || null : text,
      isMine: true,
      pending: true,
      failed: false,
      localUrl: localUrl ?? retryOf?.localUrl,
      localFile: file ?? retryOf?.localFile,
      durationSeconds: durationSeconds ?? retryOf?.durationSeconds,
      fileContentType: (file ?? retryOf?.localFile)?.type,
      hasFile: Boolean(file ?? retryOf?.localFile),
      createDate: retryOf?.createDate ?? new Date().toISOString(),
    };
    const replaceOptimistic = (patch) =>
      get().updateThread(id, (thread) => ({
        messages: thread.messages.map((m) => (m.clientMessageId === clientMessageId && m.pending ? { ...m, ...patch } : m)),
      }));

    get().updateThread(id, (thread) => ({
      messages: retryOf
        ? thread.messages.map((m) => (m.clientMessageId === clientMessageId ? optimistic : m))
        : [...thread.messages, optimistic],
    }));

    try {
      const sent =
        type === MESSAGE_TYPE.TEXT
          ? await sendText({ conversationId: id, text, clientMessageId })
          : await sendMedia({
              conversationId: id,
              file: optimistic.localFile,
              kind,
              durationSeconds: optimistic.durationSeconds,
              caption: text,
              clientMessageId,
            });
      // keep the local preview URL so the image/voice does not flash while re-downloading
      get().updateThread(id, (thread) => ({
        messages: mergeMessages(thread.messages, [{ ...sent, localUrl: optimistic.localUrl }]),
      }));
      get().patchConversation({
        id,
        lastMessage: { id: sent.id, type: sent.type, text: sent.text, isMine: true, createDate: sent.createDate },
        lastMessageAt: sent.createDate,
      });
      return sent;
    } catch (error) {
      replaceOptimistic({ failed: true, error: error.message });
      throw error;
    }
  },

  discardMessage: (id, clientMessageId) =>
    get().updateThread(id, (thread) => ({
      messages: thread.messages.filter((m) => m.clientMessageId !== clientMessageId || !m.pending),
    })),

  reset: () =>
    set({
      unreadTotal: 0,
      latestUnreadId: 0,
      conversations: [],
      conversationsLoaded: false,
      conversationsError: "",
      activeId: null,
      threads: {},
    }),
}));

export default useChatStore;
